// ==========================================================================================
// NOMBRE DEL ARCHIVO: src/components/QuotePublicView.jsx
// DESCRIPCIÓN: Descarga automática de PDF de cotización - VERSIÓN CORREGIDA
// VERSIÓN: 4.0 - Corrige: extraHours, descarga doble, datos del cliente
// ==========================================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';

// Importar el generador de PDF del sistema
import { downloadQuotePDF } from '../services/pdfQuote';

// API URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function QuotePublicView({ quoteNumber: propQuoteNumber, products = [] }) {
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  
  // Ref para evitar doble descarga
  const hasDownloaded = useRef(false);

  // Obtener parámetros de la URL
  const params = new URLSearchParams(window.location.search);
  const quoteNumber = propQuoteNumber || params.get('quote') || params.get('quoteNumber');

  useEffect(() => {
    if (!quoteNumber) {
      setStatus('error');
      setErrorMsg('No se especificó número de cotización');
      return;
    }

    // Evitar doble ejecución
    if (hasDownloaded.current) return;
    hasDownloaded.current = true;

    loadAndGeneratePDF();
  }, [quoteNumber]);

  const loadAndGeneratePDF = async () => {
    setStatus('loading');

    try {
      // 1. Cargar cotización desde la API
      const response = await fetch(`${API_BASE}/api/quotes/${quoteNumber}`);
      const data = await response.json();

      if (!data.ok || !data.quote) {
        throw new Error(data.error || 'Cotización no encontrada');
      }

      const quote = data.quote;
      console.log('✅ Cotización cargada:', quote.quoteNumber);

      setStatus('generating');

      // 2. Extraer datos
      const client = quote.client || quote.clientData || {};
      const event = quote.eventDetails || {};
      const transport = quote.transport || {};
      const financials = quote.financials || {};
      const items = quote.items || [];

      // 3. Enriquecer items con imágenes del catálogo
      const itemsConImagenes = items.map(item => {
        const producto = products.find(p =>
          p.codigo === item.productCode ||
          p.nombre === item.productName
        );

        return {
          productCode: item.productCode || '',
          productName: item.productName || '',
          quantity: item.quantity || 1,
          hours: item.hours || 5,
          unitPrice: item.unitPrice || 0,
          lineTotal: item.lineTotal || 0,
          categoria: item.category || item.categoria || '',
          cover: item.cover || producto?.imagen_principal || producto?.link_foto || producto?.images?.[0] || '',
          allImages: item.allImages || producto?.images || [],
          productSpecs: item.productSpecs || {}
        };
      });

      // 4. IMPORTANTE: Leer horas extra de financials
      const extraHours = financials.extraHours || quote.extraHours || 0;
      const extraCost = financials.extraHoursCost || quote.extraCost || 0;

      console.log('⏰ Horas extra:', extraHours, '=', extraCost);

      // 5. Construir payload para pdfQuote.js
      const pdfPayload = {
        quoteNumber: quote.quoteNumber,
        date: quote.date ? new Date(quote.date).toLocaleDateString('es-CO') : new Date().toLocaleDateString('es-CO'),
        client: {
          name: client.name || '',
          email: client.email || '',
          phone: client.phone || '',
          company: client.company || 'Particular',
          city: client.city || ''
        },
        type: quote.type || 'alquiler',
        items: itemsConImagenes,
        subtotal: financials.subtotal || quote.subtotal || 0,
        
        // ⭐ IMPORTANTE: Pasar horas extra correctamente
        extraHours: extraHours,
        extraCost: extraCost,
        
        ivaPercent: financials.ivaPercent || quote.ivaPercent || 19,
        ivaAmount: financials.ivaAmount || quote.ivaAmount || 0,
        transport: {
          city: event.city || transport.city || '',
          address: event.address || transport.address || '',
          km: event.distanceKm || transport.km || 0,
          cost: financials.transportCost || transport.cost || 0,
          volumenTotalM3: event.volumenM3 || transport.volumenTotalM3 || 0
        },
        discountPercent: financials.discountPercent || 0,
        discountAmount:  financials.discountAmount  || 0,
        total: financials.total || quote.total || 0,
        notes: quote.notes || '',
        schedule: {
          start: event.startTime || quote.schedule?.start || '',
          end: event.endTime || quote.schedule?.end || ''
        },
        company: {
          name: 'Inflables Da Silva',
          headquarters: [{
            address: 'Chía, Cundinamarca, Colombia',
            phone: '+57 3334008723 - 3112952770'
          }],
          country: 'Colombia'
        },
        conditions: quote.conditions || []
      };

      console.log('📄 Generando PDF con:', {
        quoteNumber: pdfPayload.quoteNumber,
        cliente: pdfPayload.client.name,
        items: pdfPayload.items.length,
        extraHours: pdfPayload.extraHours,
        extraCost: pdfPayload.extraCost,
        horario: `${pdfPayload.schedule.start} - ${pdfPayload.schedule.end}`
      });

      // 6. Generar y descargar PDF
      await downloadQuotePDF(pdfPayload);

      setStatus('success');
      console.log('✅ PDF generado exitosamente');

    } catch (err) {
      console.error('❌ Error:', err);
      setStatus('error');
      setErrorMsg(err.message || 'Error al generar la cotización');
    }
  };

  // --- RENDER ---
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        p: 3
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>Cargando cotización...</Typography>
        </>
      )}

      {status === 'generating' && (
        <>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>Generando PDF...</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {quoteNumber}
          </Typography>
        </>
      )}

      {status === 'success' && (
        <Alert severity="success" sx={{ maxWidth: 400 }}>
          <Typography variant="h6">¡PDF Generado!</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Tu cotización <strong>{quoteNumber}</strong> se ha descargado.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Revisa tu carpeta de descargas.
          </Typography>
        </Alert>
      )}

      {status === 'error' && (
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          <Typography variant="h6">Error</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {errorMsg}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
